import { z } from 'zod';

// «Agregar a Slack»: Estateflow owns ONE distributed Slack app; each workspace (and the platform itself) installs it with
// OAuth v2 and picks a channel per purpose (workspace: contingency alerts; platform: alerts + weekly report). The bot
// token is stored encrypted and never returned. Delivery is «sin verificar» until a test post returns ok:true. The
// pasted Incoming Webhook stays as a fallback («Usar un webhook en su lugar»).

const Iso = z.string();
const Id = z.string().min(1).max(64);

/** Bot scopes requested on install (no user scopes). */
export const SLACK_BOT_SCOPES = ['chat:write', 'chat:write.public', 'channels:read', 'groups:read'] as const;

export const SLACK_PURPOSES = ['alerts', 'weekly_report'] as const;
export const SlackPurposeSchema = z.enum(SLACK_PURPOSES);
export type SlackPurpose = z.infer<typeof SlackPurposeSchema>;
export const SLACK_PURPOSE_LABELS: Record<SlackPurpose, string> = { alerts: 'Alertas de contingencia', weekly_report: 'Reporte semanal' };

export const SlackInstallationSchema = z.object({
  id: Id,
  teamId: z.string(),
  teamName: z.string(),
  botUserId: z.string().nullable(),
  scopes: z.array(z.string()),
  /** `revoked`: Slack rejected the token (token_revoked / account_inactive / invalid_auth) → reconnect. */
  status: z.enum(['active', 'revoked']),
  revokedAt: Iso.nullable(),
  revokedReason: z.string().nullable(),
  /** A test post through the app returned ok:true at least once. */
  verifiedAt: Iso.nullable(),
  installedAt: Iso,
});
export type SlackInstallation = z.infer<typeof SlackInstallationSchema>;

export const SlackChannelChoiceSchema = z.object({
  purpose: SlackPurposeSchema,
  channelId: z.string(),
  channelName: z.string(),
  /** Only a successful test post (ok:true) verifies the channel; until then «sin verificar». */
  verified: z.boolean(),
  verifiedAt: Iso.nullable(),
  lastTestAt: Iso.nullable(),
  lastError: z.string().nullable(),
});
export type SlackChannelChoice = z.infer<typeof SlackChannelChoiceSchema>;

export const SlackStatusSchema = z.object({
  /** The platform Slack app is configured (SLACK_CLIENT_ID/SECRET); otherwise only the webhook fallback works. */
  available: z.boolean(),
  scope: z.enum(['workspace', 'platform']),
  /** Purposes that can be routed through the app in this scope. */
  purposes: z.array(SlackPurposeSchema),
  installation: SlackInstallationSchema.nullable(),
  channels: z.array(SlackChannelChoiceSchema),
});
export type SlackStatus = z.infer<typeof SlackStatusSchema>;

/** Platform installs say where to come back after Slack (Contingencia or Reporte semanal). */
export const SLACK_RETURN_PAGES = ['contingencia', 'reporte-semanal'] as const;
export const StartSlackOauthSchema = z.object({ returnTo: z.enum(SLACK_RETURN_PAGES).optional() });
export const StartSlackOauthResultSchema = z.object({ authorizeUrl: z.string(), expiresAt: Iso });

export const SlackOauthCallbackQuerySchema = z.object({
  code: z.string().max(2000).optional(),
  state: z.string().max(200).optional(),
  error: z.string().max(200).optional(),
});
export const SlackOauthCallbackResponseSchema = z.object({}).passthrough();

export const SlackConversationSchema = z.object({
  id: z.string(),
  name: z.string(),
  isPrivate: z.boolean(),
  /** The bot is a member (required for private channels; public ones work with chat:write.public). */
  isMember: z.boolean(),
});
export type SlackConversation = z.infer<typeof SlackConversationSchema>;
export const SlackConversationListSchema = z.object({
  items: z.array(SlackConversationSchema),
  /** The listing stopped at the cap (very large workspaces). */
  truncated: z.boolean(),
});

export const SelectSlackChannelSchema = z.object({ purpose: SlackPurposeSchema, channelId: z.string().trim().min(1).max(40) });
export const TestSlackChannelSchema = z.object({ purpose: SlackPurposeSchema });
export const SlackAppTestResultSchema = z.object({
  ok: z.boolean(),
  detail: z.string(),
  /** Slack's error code when ok:false (e.g. not_in_channel). */
  code: z.string().nullable(),
  status: SlackStatusSchema,
});
export type SlackAppTestResult = z.infer<typeof SlackAppTestResultSchema>;

/** Reasons the callback redirects with (`slack=error&motivo=…`) → Spanish copy for the screen. */
export const SLACK_OAUTH_ERROR_LABELS: Record<string, string> = {
  state_missing: 'La respuesta de Slack no trae el estado de la solicitud. Intenta de nuevo.',
  state_invalid: 'La solicitud expiró o ya se usó. Vuelve a presionar «Agregar a Slack».',
  session_mismatch: 'La autorización se completó en otra sesión. Vuelve a intentarlo desde esta ventana.',
  forbidden: 'Tu cuenta ya no tiene permiso para conectar Slack aquí.',
  denied: 'Se canceló la autorización en Slack.',
  code_missing: 'Slack no devolvió el código de autorización.',
  unavailable: 'Slack no está configurado en la plataforma.',
  exchange_failed: 'Slack rechazó el intercambio del código. Intenta de nuevo.',
};
